import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, User, Chrome, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import PasswordInput from '../components/PasswordInput';
import LanguageSelector from '../components/LanguageSelector';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, register: authRegister } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If already logged in, redirect to home
    if (user) {
      navigate('/');
    }
  }, [user]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error(t('auth.registerError'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('auth.registerError'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('auth.registerError'));
      return;
    }

    setLoading(true);
    
    try {
      await authRegister(name, email, password);
      toast.success(t('auth.registerSuccess'));
      navigate('/');
    } catch (error) {
      console.error('Register error:', error);
      const message = error.response?.data?.detail || t('auth.registerError');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAppleLogin = async () => {
    try {
      if (typeof window.AppleID === 'undefined') {
        toast.error('Apple Sign In não disponível. Use Google ou Email.');
        return;
      }
      
      const AppleID = window.AppleID;
      
      const currentOrigin = window.location.origin;
      console.log('🌐 Domínio atual:', currentOrigin);
      
      AppleID.auth.init({
        clientId: 'com.maiRecipes.service',
        scope: 'name email',
        redirectURI: currentOrigin,
        usePopup: true
      });
      
      const data = await AppleID.auth.signIn();
      
      const idToken = data?.authorization?.id_token;
      
      if (!idToken) {
        throw new Error('Token não recebido');
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/apple`,
        {
          id_token: idToken,
          user: data.user
        },
        { withCredentials: true }
      );
      
      toast.success('Conta criada com sucesso!');
      
      // Redirect
      setTimeout(() => {
        navigate('/');
      }, 500);
    } catch (error) {
      console.error('Apple register error:', error);
      
      if (error.error === 'popup_closed_by_user') {
        toast.info('Cadastro cancelado');
      } else {
        toast.error('Erro ao criar conta. Use Google ou Email.');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4">
      {/* Language Selector - Canto superior direito */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <Card className="w-full max-w-md p-8 shadow-2xl border-2 border-amber-100">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/login')}
          variant="ghost"
          className="mb-4 -ml-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>

        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="mAI Recipes" className="w-40 h-40 object-contain" />
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-center text-amber-900 mb-2">{t('auth.createAccount')}</h1>
        <p className="text-center text-gray-600 mb-8">{t('auth.register')}</p>

        {/* Social Login Buttons - DESABILITADOS TEMPORARIAMENTE */}
        {/*
        <div className="space-y-3 mb-6">
          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full py-6 border-2 border-gray-300 hover:border-orange-400 hover:bg-orange-50 transition-all"
          >
            <Chrome className="w-5 h-5 mr-2" />
            {t('auth.signUpWith')} {t('auth.google')}
          </Button>

          <Button
            onClick={handleAppleLogin}
            variant="outline"
            className="w-full py-6 border-2 border-gray-300 hover:border-orange-400 hover:bg-orange-50 transition-all"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            {t('auth.signUpWith')} {t('auth.apple')}
          </Button>
        </div>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">{t('auth.orContinueWith')}</span>
          </div>
        </div>
        */}

        {/* Register Form */}
        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.name')}</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('auth.name')}
                className="pl-11"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.email')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="pl-11"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.password')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.password')}
                className="pl-11"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.password')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth.password')}
                className="pl-11"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white py-6 mt-6"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <User className="w-5 h-5 mr-2" />
                {t('auth.register')}
              </>
            )}
          </Button>
        </form>

        {/* Login Link */}
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            {t('auth.alreadyHaveAccount')}{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-orange-600 hover:text-orange-700 font-semibold"
            >
              {t('auth.login')}
            </button>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default RegisterPage;
